/**
 * Deterministic PRNG. Same seed + same call sequence => same numbers.
 * Underpins daily challenges and replay verification (spec §3.7).
 *
 * Uses xmur3 for string-seed hashing and mulberry32 for the stream.
 */

function xmur3(str: string): () => number {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return () => {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    h ^= h >>> 16;
    return h >>> 0;
  };
}

function mulberry32(a: number): () => number {
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export class Rng {
  private next: () => number;
  readonly seed: string;

  constructor(seed: string) {
    this.seed = seed;
    const seeder = xmur3(seed);
    this.next = mulberry32(seeder());
  }

  /** Float in [0, 1). */
  float(): number {
    return this.next();
  }

  /** Integer in [0, max). */
  int(max: number): number {
    return Math.floor(this.next() * max);
  }

  /**
   * Pick a key from a weighted table. Keys are tier levels (as strings).
   * Weights need not sum to 1.
   */
  weighted(table: Record<string, number>): number {
    return Number(this.weightedKey(table));
  }

  /**
   * Same as `weighted` but returns the raw key string — needed when the table
   * mixes tier numbers with special tokens ("wild", "bomb").
   */
  weightedKey(table: Record<string, number>): string {
    const entries = Object.entries(table);
    let total = 0;
    for (const [, w] of entries) total += w;
    let roll = this.next() * total;
    for (const [key, w] of entries) {
      roll -= w;
      if (roll < 0) return key;
    }
    // Floating-point fallback: return last key.
    return entries[entries.length - 1][0];
  }
}
